import { BaseFeature } from '../../core/base-feature.js';
import { coordinateMapper } from '../../src/utils/CoordinateMapper.js';

/**
 * WindowControlFeature - Window manipulation and tracking (Frontend)
 * Handles: screen click detection, window outline rendering, window manipulation UI
 */
export class WindowControlFeature extends BaseFeature {
    constructor(coreAPI) {
        super(coreAPI);

        // Window tracking state
        this.selectedWindow = null;
        this.selectedWindowPeerId = null;
        this.foregroundWindow = null; // Currently focused window on desktop
        this.overlayMappings = {}; // Map of overlayHwnd -> targetHwnd
        this.windowStates = new Map(); // hwnd -> state object

        // Time tracking for focused windows
        this.focusedWindowStartTime = null; // When current window gained focus
        this.windowFocusTimes = new Map(); // hwnd -> { totalFocusMs, openedAt }
        this.focusTimeUpdateInterval = null;

        // Native screen resolution (from desktop) for proper coordinate scaling
        // when video stream is constrained to lower resolution
        this.nativeScreenWidth = null;
        this.nativeScreenHeight = null;

        // DOM element references
        this.remoteVideos = null;
        this.windowPropertiesContent = null;
        this.cameraPreviewContainer = null;
    }

    static getMetadata() {
        return {
            id: 'window-control',
            name: 'window-control',
            version: 'v1.2',
            capabilities: [
                'handle_screen_click',
                'render_window_outline',
                'manipulate_window',
                'track_window_updates'
            ],
            dependencies: []
        };
    }

    async initialize() {
        console.log('[WindowControl] Initializing...');

        // DOM elements are looked up dynamically since they may not exist at init time
        // and may change as React routes change (components mount/unmount)

        // Add escape key handler to clear selection
        this.handleEscapeKey = (e) => {
            if (e.key === 'Escape') {
                console.log('[WindowControl] Escape pressed - clearing selection');
                this.clearAllOutlines();
                this.selectedWindow = null;
                this.selectedWindowPeerId = null;
                this.emit('selectedWindowUpdate', null);
            }
        };
        document.addEventListener('keydown', this.handleEscapeKey);

        console.log('[WindowControl] ✓ Initialized');
    }

    /**
     * Get windowPropertiesContent element dynamically
     * Returns null if element doesn't exist on the current page
     */
    getWindowPropertiesContent() {
        return document.getElementById('windowPropertiesContent');
    }

    async handleMessage(message) {
        const { action, payload, messageId, fromId } = message;

        switch (action) {
            case 'get_window_at_position_response':
                return await this.handleWindowInfoResponse(payload, messageId, fromId);

            case 'window_position_update':
                return this.updateWindowOutline(payload.windowInfo);

            case 'window_closed':
                return this.handleWindowClosed(payload.hwnd);

            case 'window_tracking_stopped':
                return this.clearAllOutlines();

            case 'overlay_state':
                this.overlayMappings = payload.overlayMappings || {};
                console.log('[WindowControl] Received overlay state:', this.overlayMappings);
                return;

            case 'foreground_window_update':
                return this.updateForegroundWindow(payload.windowInfo);

            // Handle response messages
            case 'enable_close_button_response':
            case 'disable_close_button_response':
            case 'set_window_title_response':
            case 'close_window_response':
            case 'enable_move_response':
            case 'disable_move_response':
            case 'show_overlay_response':
            case 'hide_overlay_response':
                return this.handleCommandResponse(action, payload);

            default:
                console.warn(`[WindowControl] Unknown action: ${action}`);
        }
    }

    handleCommandResponse(action, payload) {
        if (payload.error) {
            console.error(`[WindowControl] ${action} failed:`, payload.error);
            alert(`Operation failed: ${payload.error}`);
        } else {
            console.log(`[WindowControl] ${action} succeeded`);
        }
    }

    /**
     * Get the currently selected peer ID from SocketContext
     * This ensures we always use the current peer, even after reconnection
     */
    getTargetPeerId() {
        if (this.core.getSelectedPeerId) {
            return this.core.getSelectedPeerId();
        }
        // Fallback to stored peerId if coreAPI method not available
        return this.selectedWindowPeerId;
    }

    /**
     * Handle screen share video click to detect windows
     * Note: peerId parameter is kept for backward compatibility but ignored.
     * We use getTargetPeerId() to get the current selected peer dynamically.
     */
    setupScreenShareClickHandler(videoElement, peerId = null) {
        // Store reference so we can remove it later if needed
        if (!videoElement._windowControlClickHandler) {
            videoElement._windowControlClickHandler = (e) => this.handleScreenShareClick(e, videoElement);
            videoElement.addEventListener('click', videoElement._windowControlClickHandler);
            videoElement.style.cursor = 'crosshair';
        }
    }

    /**
     * Remove the click handler from a video element
     * Used when switching to remote control mode
     */
    removeScreenShareClickHandler(videoElement) {
        if (videoElement && videoElement._windowControlClickHandler) {
            videoElement.removeEventListener('click', videoElement._windowControlClickHandler);
            delete videoElement._windowControlClickHandler;
            videoElement.style.cursor = '';
        }
    }

    async handleScreenShareClick(event, videoElement) {
        // Get click position relative to video element
        const rect = videoElement.getBoundingClientRect();
        const clickX = event.clientX - rect.left;
        const clickY = event.clientY - rect.top;

        // Calculate normalized coordinates (0-1)
        const normalizedX = clickX / rect.width;
        const normalizedY = clickY / rect.height;

        // Use native screen resolution if available (for constrained video streams)
        // Fall back to video intrinsic dimensions if not available
        const targetWidth = this.nativeScreenWidth || videoElement.videoWidth;
        const targetHeight = this.nativeScreenHeight || videoElement.videoHeight;

        // Calculate absolute screen coordinates in native resolution
        const screenX = Math.round(normalizedX * targetWidth);
        const screenY = Math.round(normalizedY * targetHeight);

        console.log(`[WindowControl] Clicked at display: (${clickX.toFixed(0)}, ${clickY.toFixed(0)}), ` +
                    `normalized: (${normalizedX.toFixed(3)}, ${normalizedY.toFixed(3)}), ` +
                    `screen: (${screenX}, ${screenY}), native: ${targetWidth}x${targetHeight}`);

        // Check if corruption spawn mode is active
        const featureManager = this.core.getFeatureManager?.();
        const corruptionFeature = featureManager?.getFeature?.('corruption');

        // Debug logging for spawn mode
        console.log('[WindowControl] Spawn mode check:', {
            hasFeatureManager: !!featureManager,
            hasCorruptionFeature: !!corruptionFeature,
            isSpawnMode: corruptionFeature?.isSpawnMode,
            spawnModeValue: corruptionFeature?.spawnMode,
            isGameActive: corruptionFeature?.isGameActive?.(),
            currentGame: corruptionFeature?.currentGame?.status
        });

        if (corruptionFeature?.isSpawnMode && corruptionFeature?.isGameActive?.()) {
            // Show spawn feedback instead of window selection feedback
            this.showSpawnFeedback(videoElement, clickX, clickY);

            // Spawn node at clicked position using normalized coordinates
            console.log('[WindowControl] Calling spawnNodeAtPosition:', normalizedX.toFixed(3), normalizedY.toFixed(3));
            corruptionFeature.spawnNodeAtPosition(normalizedX, normalizedY);
            console.log('[WindowControl] Spawn mode active - spawning node instead of selecting window');
            return;
        }

        // Show immediate visual feedback at click location
        this.showClickFeedback(videoElement, clickX, clickY);

        // Get current selected peer dynamically (not captured at setup time)
        const targetPeerId = this.getTargetPeerId();
        console.log('[WindowControl] Sending get_window_at_position to peer:', targetPeerId?.substring(0, 8) || 'broadcast');
        this.sendMessage('get_window_at_position', {
            x: screenX,
            y: screenY
        }, targetPeerId);
        console.log('[WindowControl] Message sent');
    }

    /**
     * Show visual feedback when spawning a node in corruption game
     */
    showSpawnFeedback(videoElement, clickX, clickY) {
        const videoWrapper = videoElement.parentElement;

        // Create a purple corruption-themed ripple effect
        const ripple = document.createElement('div');
        ripple.style.cssText = `
            position: absolute;
            left: ${clickX - 15}px;
            top: ${clickY - 15}px;
            width: 30px;
            height: 30px;
            border: 3px solid #a855f7;
            border-radius: 50%;
            pointer-events: none;
            z-index: 1000;
            animation: spawn-ripple 0.6s ease-out;
            box-shadow: 0 0 15px rgba(168, 85, 247, 0.6);
        `;

        // Ensure video wrapper is positioned
        if (videoWrapper.style.position !== 'absolute' && videoWrapper.style.position !== 'relative') {
            videoWrapper.style.position = 'relative';
        }

        videoWrapper.appendChild(ripple);

        // Add animation if not already in styles
        if (!document.getElementById('spawn-ripple-animation')) {
            const style = document.createElement('style');
            style.id = 'spawn-ripple-animation';
            style.textContent = `
                @keyframes spawn-ripple {
                    0% {
                        transform: scale(0.5);
                        opacity: 1;
                        border-color: #ff00ff;
                    }
                    50% {
                        border-color: #a855f7;
                    }
                    100% {
                        transform: scale(2.5);
                        opacity: 0;
                        border-color: #7c3aed;
                    }
                }
            `;
            document.head.appendChild(style);
        }

        // Remove after animation
        setTimeout(() => ripple.remove(), 600);
    }

    showClickFeedback(videoElement, clickX, clickY) {
        const videoWrapper = videoElement.parentElement;

        // Create a ripple effect at click location
        const ripple = document.createElement('div');
        ripple.style.cssText = `
            position: absolute;
            left: ${clickX - 10}px;
            top: ${clickY - 10}px;
            width: 20px;
            height: 20px;
            border: 2px solid #2563eb;
            border-radius: 50%;
            pointer-events: none;
            z-index: 1000;
            animation: ripple 0.5s ease-out;
        `;

        // Ensure video wrapper is positioned
        if (videoWrapper.style.position !== 'absolute' && videoWrapper.style.position !== 'relative') {
            videoWrapper.style.position = 'relative';
        }

        videoWrapper.appendChild(ripple);

        // Add animation if not already in styles
        if (!document.getElementById('ripple-animation')) {
            const style = document.createElement('style');
            style.id = 'ripple-animation';
            style.textContent = `
                @keyframes ripple {
                    0% {
                        transform: scale(1);
                        opacity: 1;
                    }
                    100% {
                        transform: scale(3);
                        opacity: 0;
                    }
                }
            `;
            document.head.appendChild(style);
        }

        // Remove after animation
        setTimeout(() => ripple.remove(), 500);
    }

    async handleWindowInfoResponse(payload, messageId, fromId) {
        const { windowInfo } = payload;

        if (!windowInfo) {
            alert('No window information available');
            return;
        }

        console.log('[WindowControl] Received window info:', windowInfo);

        // Store native screen resolution for coordinate conversion
        if (windowInfo.screenWidth && windowInfo.screenHeight) {
            this.nativeScreenWidth = windowInfo.screenWidth;
            this.nativeScreenHeight = windowInfo.screenHeight;
            // Update global coordinate mapper for remote control
            coordinateMapper.setNativeResolution(windowInfo.screenWidth, windowInfo.screenHeight);
        }

        // Clear any previous selection before selecting new window
        if (!this.selectedWindow || this.selectedWindow.hwnd !== windowInfo.hwnd) {
            this.clearSelectedWindow();
        }

        // Store selected window info
        this.selectedWindow = windowInfo;
        this.selectedWindowPeerId = fromId;

        // Emit event for React components to update
        this.emit('selectedWindowUpdate', windowInfo);

        // Draw window outline on the screen share video
        this.drawWindowOutline(windowInfo);

        // Update properties panel
        this.updatePropertiesPanel(windowInfo);
    }

    drawWindowOutline(windowInfo) {
        // Find all screen share video elements
        const screenShareVideos = document.querySelectorAll('[data-type="Screen-Share"] video');

        screenShareVideos.forEach(video => {
            const videoWrapper = video.parentElement;

            // Remove any existing outlines
            const existingOutlines = videoWrapper.querySelectorAll('.window-outline');
            existingOutlines.forEach(outline => outline.remove());

            // Get display dimensions (CSS rendered size)
            const displayWidth = video.clientWidth;
            const displayHeight = video.clientHeight;

            // Use native screen resolution if provided (for constrained video streams)
            // Fall back to video intrinsic dimensions if not available
            const sourceWidth = windowInfo.screenWidth || video.videoWidth;
            const sourceHeight = windowInfo.screenHeight || video.videoHeight;

            // Calculate scale factors from native screen to display
            const scaleX = displayWidth / sourceWidth;
            const scaleY = displayHeight / sourceHeight;

            // Convert window coordinates to display coordinates
            const outlineX = windowInfo.x * scaleX;
            const outlineY = windowInfo.y * scaleY;
            const outlineWidth = windowInfo.width * scaleX;
            const outlineHeight = windowInfo.height * scaleY;

            // Create outline element (GREEN for selected/targeted window)
            const outline = document.createElement('div');
            outline.className = 'window-outline';
            outline.style.cssText = `
                position: absolute;
                left: ${outlineX}px;
                top: ${outlineY}px;
                width: ${outlineWidth}px;
                height: ${outlineHeight}px;
                border: 2px solid rgba(34, 197, 94, 0.9);
                box-shadow: inset 0 0 0 1px rgba(34, 197, 94, 0.3), 0 0 12px rgba(34, 197, 94, 0.25);
                pointer-events: none;
                z-index: 100;
                box-sizing: border-box;
                border-radius: 3px;
            `;

            // Create label notch for selected/targeted window
            const label = document.createElement('div');
            label.className = 'window-label';
            label.textContent = 'TARGETED';
            label.style.cssText = `
                position: absolute;
                left: ${outlineX + outlineWidth - 80}px;
                top: ${Math.max(0, outlineY - 22)}px;
                background: rgba(34, 197, 94, 0.95);
                color: #052e16;
                padding: 3px 10px;
                border-radius: 4px 4px 0 0;
                font-size: 10px;
                font-weight: 600;
                letter-spacing: 0.5px;
                pointer-events: none;
                z-index: 101;
                white-space: nowrap;
                font-family: system-ui, -apple-system, sans-serif;
                text-transform: uppercase;
            `;

            // Ensure video wrapper is positioned
            if (videoWrapper.style.position !== 'absolute' && videoWrapper.style.position !== 'relative') {
                videoWrapper.style.position = 'relative';
            }

            videoWrapper.appendChild(outline);
            videoWrapper.appendChild(label);

            // Auto-remove after 5 seconds
            setTimeout(() => {
                outline.remove();
                label.remove();
            }, 5000);
        });
    }

    updateWindowOutline(windowInfo) {
        // Store native screen resolution for coordinate conversion
        if (windowInfo.screenWidth && windowInfo.screenHeight) {
            this.nativeScreenWidth = windowInfo.screenWidth;
            this.nativeScreenHeight = windowInfo.screenHeight;
            // Update global coordinate mapper for remote control
            coordinateMapper.setNativeResolution(windowInfo.screenWidth, windowInfo.screenHeight);
        }

        // If this is a different window, clear the previous selection
        if (!this.selectedWindow || this.selectedWindow.hwnd !== windowInfo.hwnd) {
            this.clearSelectedWindow();
            // Store the new selected window
            this.selectedWindow = windowInfo;
            this.selectedWindowPeerId = this.selectedWindowPeerId; // Keep existing peer ID
        } else {
            // Same window - update position in place to keep drag handler reference valid
            this.selectedWindow.x = windowInfo.x;
            this.selectedWindow.y = windowInfo.y;
            this.selectedWindow.width = windowInfo.width;
            this.selectedWindow.height = windowInfo.height;
            this.selectedWindow.title = windowInfo.title;
        }

        // Find all screen share video elements
        const screenShareVideos = document.querySelectorAll('[data-type="Screen-Share"] video');

        screenShareVideos.forEach(video => {
            const videoWrapper = video.parentElement;

            // Find or create elements for this window (identified by hwnd)
            let existingOutline = videoWrapper.querySelector(`.window-outline[data-hwnd="${windowInfo.hwnd}"]`);
            let existingLabel = videoWrapper.querySelector(`.window-label[data-hwnd="${windowInfo.hwnd}"]`);
            let existingShade = videoWrapper.querySelector(`.window-shade[data-hwnd="${windowInfo.hwnd}"]`);

            // Get display dimensions (CSS rendered size)
            const displayWidth = video.clientWidth;
            const displayHeight = video.clientHeight;

            // Use native screen resolution if provided (for constrained video streams)
            // Fall back to video intrinsic dimensions if not available
            const sourceWidth = windowInfo.screenWidth || video.videoWidth;
            const sourceHeight = windowInfo.screenHeight || video.videoHeight;

            // Calculate scale factors from native screen to display
            const scaleX = displayWidth / sourceWidth;
            const scaleY = displayHeight / sourceHeight;

            // Convert window coordinates to display coordinates
            const outlineX = windowInfo.x * scaleX;
            const outlineY = windowInfo.y * scaleY;
            const outlineWidth = windowInfo.width * scaleX;
            const outlineHeight = windowInfo.height * scaleY;

            // Ensure video wrapper is positioned
            if (videoWrapper.style.position !== 'absolute' && videoWrapper.style.position !== 'relative') {
                videoWrapper.style.position = 'relative';
            }

            // Create/update shade (10% opacity overlay)
            if (!existingShade) {
                existingShade = document.createElement('div');
                existingShade.className = 'window-shade';
                existingShade.setAttribute('data-hwnd', windowInfo.hwnd);
                videoWrapper.appendChild(existingShade);
            }
            existingShade.style.left = `${outlineX}px`;
            existingShade.style.top = `${outlineY}px`;
            existingShade.style.width = `${outlineWidth}px`;
            existingShade.style.height = `${outlineHeight}px`;

            // Create/update outline if it doesn't exist (GREEN for selected/targeted window)
            if (!existingOutline) {
                existingOutline = document.createElement('div');
                existingOutline.className = 'window-outline';
                existingOutline.setAttribute('data-hwnd', windowInfo.hwnd);
                existingOutline.style.cssText = `
                    position: absolute;
                    border: 2px solid rgba(34, 197, 94, 0.9);
                    box-shadow: inset 0 0 0 1px rgba(34, 197, 94, 0.3), 0 0 12px rgba(34, 197, 94, 0.25);
                    pointer-events: none;
                    z-index: 100;
                    box-sizing: border-box;
                    border-radius: 3px;
                `;
                videoWrapper.appendChild(existingOutline);
            }

            // Update outline position and size
            existingOutline.style.left = `${outlineX}px`;
            existingOutline.style.top = `${outlineY}px`;
            existingOutline.style.width = `${outlineWidth}px`;
            existingOutline.style.height = `${outlineHeight}px`;

            // Create/update drag handle at bottom center (GREEN for targeted window)
            let existingDragHandle = videoWrapper.querySelector(`.window-drag-handle[data-hwnd="${windowInfo.hwnd}"]`);
            if (!existingDragHandle) {
                existingDragHandle = document.createElement('div');
                existingDragHandle.className = 'window-drag-handle';
                existingDragHandle.setAttribute('data-hwnd', windowInfo.hwnd);
                existingDragHandle.innerHTML = `<span style="font-size: 16px; user-select: none;">⋮⋮</span>`;
                existingDragHandle.style.cssText = `
                    position: absolute;
                    padding: 4px 10px;
                    background: rgba(20, 20, 20, 0.9);
                    border: 1px solid rgba(34, 197, 94, 0.6);
                    border-radius: 4px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    pointer-events: auto;
                    cursor: grab;
                    z-index: 102;
                    color: #22c55e;
                    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.4), 0 0 8px rgba(34, 197, 94, 0.2);
                    transition: transform 0.1s, box-shadow 0.1s;
                    font-family: monospace;
                    backdrop-filter: blur(4px);
                `;

                // Add hover effect
                existingDragHandle.addEventListener('mouseenter', () => {
                    existingDragHandle.style.transform = 'translateY(-2px)';
                    existingDragHandle.style.boxShadow = '0 4px 10px rgba(0, 0, 0, 0.5), 0 0 12px rgba(34, 197, 94, 0.4)';
                });
                existingDragHandle.addEventListener('mouseleave', () => {
                    existingDragHandle.style.transform = 'translateY(0)';
                    existingDragHandle.style.boxShadow = '0 2px 6px rgba(0, 0, 0, 0.4), 0 0 8px rgba(34, 197, 94, 0.2)';
                });

                videoWrapper.appendChild(existingDragHandle);

                // Add drag handlers to the handle
                this.setupDragHandlers(existingDragHandle, video);
            }

            // Position drag handle at bottom center of outline, inside the window
            existingDragHandle.style.left = `${outlineX + outlineWidth / 2 - 20}px`;
            existingDragHandle.style.top = `${outlineY + outlineHeight - 35}px`;

            // Create/update label (GREEN notch for targeted window)
            if (!existingLabel) {
                existingLabel = document.createElement('div');
                existingLabel.className = 'window-label';
                existingLabel.setAttribute('data-hwnd', windowInfo.hwnd);
                existingLabel.style.cssText = `
                    position: absolute;
                    background: rgba(34, 197, 94, 0.95);
                    color: #052e16;
                    padding: 3px 10px;
                    border-radius: 4px 4px 0 0;
                    font-size: 10px;
                    font-weight: 600;
                    letter-spacing: 0.5px;
                    pointer-events: none;
                    z-index: 101;
                    white-space: nowrap;
                    font-family: system-ui, -apple-system, sans-serif;
                    text-transform: uppercase;
                `;
                videoWrapper.appendChild(existingLabel);
            }

            existingLabel.style.left = `${outlineX + outlineWidth - 80}px`;
            existingLabel.style.top = `${Math.max(0, outlineY - 22)}px`;
            existingLabel.style.maxWidth = `80px`;
            existingLabel.textContent = 'TARGETED';

        });
    }

    /**
     * Update foreground window outline (BLUE border)
     * This shows which window currently has focus on the desktop
     */
    updateForegroundWindow(windowInfo) {
        // Store native screen resolution for coordinate conversion
        if (windowInfo && windowInfo.screenWidth && windowInfo.screenHeight) {
            this.nativeScreenWidth = windowInfo.screenWidth;
            this.nativeScreenHeight = windowInfo.screenHeight;
            // Update global coordinate mapper for remote control
            coordinateMapper.setNativeResolution(windowInfo.screenWidth, windowInfo.screenHeight);
        }

        // If no window info (e.g., focus lost), clear foreground outline
        if (!windowInfo || !windowInfo.hwnd) {
            this.stopFocusTimeTracking();
            this.clearForegroundOutline();
            this.foregroundWindow = null;
            this.emit('foregroundWindowUpdate', null);
            return;
        }

        // Don't show foreground outline if it's the same as selected window
        if (this.selectedWindow && this.selectedWindow.hwnd === windowInfo.hwnd) {
            this.stopFocusTimeTracking();
            this.clearForegroundOutline();
            this.foregroundWindow = null;
            this.emit('foregroundWindowUpdate', null);
            return;
        }

        // Track focus time - if this is a new window, start tracking
        const previousHwnd = this.foregroundWindow?.hwnd;
        if (previousHwnd !== windowInfo.hwnd) {
            this.startFocusTimeTracking(windowInfo.hwnd);
        }

        this.foregroundWindow = windowInfo;
        this.emit('foregroundWindowUpdate', windowInfo);

        // Find all screen share video elements
        const screenShareVideos = document.querySelectorAll('[data-type="Screen-Share"] video');

        screenShareVideos.forEach(video => {
            const videoWrapper = video.parentElement;

            // Find or create foreground outline element
            let existingOutline = videoWrapper.querySelector('.foreground-window-outline');
            let existingLabel = videoWrapper.querySelector('.foreground-window-label');

            // Get display dimensions (CSS rendered size)
            const displayWidth = video.clientWidth;
            const displayHeight = video.clientHeight;

            // Use native screen resolution if provided
            const sourceWidth = windowInfo.screenWidth || video.videoWidth;
            const sourceHeight = windowInfo.screenHeight || video.videoHeight;

            // Calculate scale factors from native screen to display
            const scaleX = displayWidth / sourceWidth;
            const scaleY = displayHeight / sourceHeight;

            // Convert window coordinates to display coordinates
            const outlineX = windowInfo.x * scaleX;
            const outlineY = windowInfo.y * scaleY;
            const outlineWidth = windowInfo.width * scaleX;
            const outlineHeight = windowInfo.height * scaleY;

            // Ensure video wrapper is positioned
            if (videoWrapper.style.position !== 'absolute' && videoWrapper.style.position !== 'relative') {
                videoWrapper.style.position = 'relative';
            }

            // Create/update foreground outline (BLUE for focused window)
            if (!existingOutline) {
                existingOutline = document.createElement('div');
                existingOutline.className = 'foreground-window-outline';
                existingOutline.style.cssText = `
                    position: absolute;
                    border: 2px solid rgba(37, 99, 235, 0.9);
                    box-shadow: inset 0 0 0 1px rgba(37, 99, 235, 0.3), 0 0 12px rgba(37, 99, 235, 0.25);
                    pointer-events: none;
                    z-index: 99;
                    box-sizing: border-box;
                    border-radius: 3px;
                `;
                videoWrapper.appendChild(existingOutline);
            }

            // Update outline position and size
            existingOutline.style.left = `${outlineX}px`;
            existingOutline.style.top = `${outlineY}px`;
            existingOutline.style.width = `${outlineWidth}px`;
            existingOutline.style.height = `${outlineHeight}px`;

            // Get time info for this window
            const timeInfo = this.getWindowTimeInfo(windowInfo.hwnd);
            const focusedTimeStr = this.formatDuration(timeInfo.currentFocusMs);
            const totalTimeStr = this.formatDuration(timeInfo.totalOpenMs);

            // Create/update label with time info
            if (!existingLabel) {
                existingLabel = document.createElement('div');
                existingLabel.className = 'foreground-window-label';
                existingLabel.style.cssText = `
                    position: absolute;
                    background: rgba(37, 99, 235, 0.95);
                    color: white;
                    padding: 3px 10px;
                    border-radius: 4px 4px 0 0;
                    font-size: 10px;
                    font-weight: 600;
                    letter-spacing: 0.5px;
                    pointer-events: none;
                    z-index: 99;
                    white-space: nowrap;
                    font-family: system-ui, -apple-system, sans-serif;
                    display: flex;
                    gap: 8px;
                    align-items: center;
                `;
                videoWrapper.appendChild(existingLabel);
            }

            existingLabel.style.left = `${outlineX + outlineWidth - 180}px`;
            existingLabel.style.top = `${Math.max(0, outlineY - 22)}px`;
            existingLabel.innerHTML = `
                <span style="text-transform: uppercase;">FOCUSED</span>
                <span style="opacity: 0.7; font-size: 9px;">|</span>
                <span style="font-variant-numeric: tabular-nums; font-weight: 500;">${focusedTimeStr}</span>
                <span style="opacity: 0.5; font-size: 9px;">/</span>
                <span style="opacity: 0.7; font-variant-numeric: tabular-nums; font-weight: 400; font-size: 9px;">${totalTimeStr}</span>
            `;
        });
    }

    /**
     * Start tracking focus time for a window
     */
    startFocusTimeTracking(hwnd) {
        // Record when focus started
        this.focusedWindowStartTime = Date.now();

        // Initialize window time tracking if not exists
        if (!this.windowFocusTimes.has(hwnd)) {
            this.windowFocusTimes.set(hwnd, {
                totalFocusMs: 0,
                openedAt: Date.now()
            });
        }

        // Start interval to update the display
        if (this.focusTimeUpdateInterval) {
            clearInterval(this.focusTimeUpdateInterval);
        }
        this.focusTimeUpdateInterval = setInterval(() => {
            // Re-render to update times
            if (this.foregroundWindow) {
                this.updateForegroundWindow(this.foregroundWindow);
            }
        }, 1000);
    }

    /**
     * Stop tracking focus time and accumulate to total
     */
    stopFocusTimeTracking() {
        if (this.focusedWindowStartTime && this.foregroundWindow) {
            const hwnd = this.foregroundWindow.hwnd;
            const elapsed = Date.now() - this.focusedWindowStartTime;

            if (this.windowFocusTimes.has(hwnd)) {
                const timeInfo = this.windowFocusTimes.get(hwnd);
                timeInfo.totalFocusMs += elapsed;
            }
        }

        this.focusedWindowStartTime = null;

        if (this.focusTimeUpdateInterval) {
            clearInterval(this.focusTimeUpdateInterval);
            this.focusTimeUpdateInterval = null;
        }
    }

    /**
     * Get time info for a window
     */
    getWindowTimeInfo(hwnd) {
        const timeInfo = this.windowFocusTimes.get(hwnd) || {
            totalFocusMs: 0,
            openedAt: Date.now()
        };

        // Calculate current focus duration if this window is focused
        let currentFocusMs = 0;
        if (this.focusedWindowStartTime && this.foregroundWindow?.hwnd === hwnd) {
            currentFocusMs = Date.now() - this.focusedWindowStartTime;
        }

        // Total open time since we first saw it
        const totalOpenMs = Date.now() - timeInfo.openedAt;

        return {
            currentFocusMs,
            totalFocusMs: timeInfo.totalFocusMs + currentFocusMs,
            totalOpenMs
        };
    }

    /**
     * Format duration in ms to human readable string
     */
    formatDuration(ms) {
        const seconds = Math.floor(ms / 1000);
        const minutes = Math.floor(seconds / 60);
        const hours = Math.floor(minutes / 60);

        if (hours > 0) {
            return `${hours}h ${minutes % 60}m`;
        } else if (minutes > 0) {
            return `${minutes}m ${seconds % 60}s`;
        } else {
            return `${seconds}s`;
        }
    }

    /**
     * Clear foreground window outline
     */
    clearForegroundOutline() {
        const outlines = document.querySelectorAll('.foreground-window-outline');
        const labels = document.querySelectorAll('.foreground-window-label');
        outlines.forEach(el => el.remove());
        labels.forEach(el => el.remove());
    }

    createWindowControlsOverlay(windowInfo) {
        const overlay = document.createElement('div');
        overlay.className = 'window-controls-overlay';
        overlay.setAttribute('data-hwnd', windowInfo.hwnd);

        const state = this.windowStates.get(windowInfo.hwnd) || {
            closeEnabled: true,
            moveEnabled: true,
            overlayEnabled: false
        };
        this.windowStates.set(windowInfo.hwnd, state);

        // Create toggle buttons
        const createToggle = (label, stateKey) => {
            const btn = document.createElement('button');
            btn.className = `window-control-btn ${state[stateKey] ? 'toggle-on' : 'toggle-off'}`;
            btn.textContent = label;
            btn.onclick = (e) => {
                e.stopPropagation();
                state[stateKey] = !state[stateKey];
                btn.className = `window-control-btn ${state[stateKey] ? 'toggle-on' : 'toggle-off'}`;
                this.toggleWindowFeature(windowInfo.hwnd, stateKey, state[stateKey]);
            };
            return btn;
        };

        overlay.appendChild(createToggle('Close', 'closeEnabled'));
        overlay.appendChild(createToggle('Input', 'moveEnabled'));
        overlay.appendChild(createToggle('Overlay', 'overlayEnabled'));

        // Close window button
        const closeBtn = document.createElement('button');
        closeBtn.className = 'window-control-btn action';
        closeBtn.textContent = 'Close Window';
        closeBtn.onclick = (e) => {
            e.stopPropagation();
            if (confirm(`Close "${windowInfo.title}"?`)) {
                this.sendMessage('close_window', { hwnd: windowInfo.hwnd }, this.getTargetPeerId());
            }
        };
        overlay.appendChild(closeBtn);

        return overlay;
    }

    toggleWindowFeature(hwnd, feature, enabled) {
        console.log(`[WindowControl] Toggling ${feature} to ${enabled} for window ${hwnd}`);

        // Map feature names to actions
        const actionMap = {
            'closeEnabled': enabled ? 'enable_close_button' : 'disable_close_button',
            'moveEnabled': enabled ? 'enable_move' : 'disable_move',
            'overlayEnabled': enabled ? 'show_overlay' : 'hide_overlay'
        };

        const action = actionMap[feature];
        if (action) {
            this.sendMessage(action, { hwnd }, this.getTargetPeerId());
        }
    }

    handleWindowClosed(hwnd) {
        console.log('[WindowControl] Window closed:', hwnd);

        // Remove all elements for this window
        const outlines = document.querySelectorAll(`.window-outline[data-hwnd="${hwnd}"]`);
        const labels = document.querySelectorAll(`.window-label[data-hwnd="${hwnd}"]`);
        const shades = document.querySelectorAll(`.window-shade[data-hwnd="${hwnd}"]`);
        const controls = document.querySelectorAll(`.window-controls-overlay[data-hwnd="${hwnd}"]`);
        const dragHandles = document.querySelectorAll(`.window-drag-handle[data-hwnd="${hwnd}"]`);

        outlines.forEach(el => el.remove());
        labels.forEach(el => el.remove());
        shades.forEach(el => el.remove());
        controls.forEach(el => el.remove());
        dragHandles.forEach(el => el.remove());
    }

    clearSelectedWindow() {
        console.log('[WindowControl] Clearing selected window');

        const outlines = document.querySelectorAll('.window-outline');
        const labels = document.querySelectorAll('.window-label');
        const shades = document.querySelectorAll('.window-shade');
        const controls = document.querySelectorAll('.window-controls-overlay');
        const dragHandles = document.querySelectorAll('.window-drag-handle');

        outlines.forEach(el => el.remove());
        labels.forEach(el => el.remove());
        shades.forEach(el => el.remove());
        controls.forEach(el => el.remove());
        dragHandles.forEach(el => el.remove());

        // Re-show foreground outline if it was hidden due to matching selected window
        if (this.foregroundWindow) {
            this.updateForegroundWindow(this.foregroundWindow);
        }
    }

    clearAllOutlines() {
        console.log('[WindowControl] Clearing all outlines');

        const outlines = document.querySelectorAll('.window-outline');
        const labels = document.querySelectorAll('.window-label');
        const shades = document.querySelectorAll('.window-shade');
        const controls = document.querySelectorAll('.window-controls-overlay');
        const dragHandles = document.querySelectorAll('.window-drag-handle');

        outlines.forEach(el => el.remove());
        labels.forEach(el => el.remove());
        shades.forEach(el => el.remove());
        controls.forEach(el => el.remove());
        dragHandles.forEach(el => el.remove());

        // Also clear foreground outline
        this.clearForegroundOutline();
        this.foregroundWindow = null;
    }

    updatePropertiesPanel(windowInfo) {
        const windowPropertiesContent = this.getWindowPropertiesContent();
        if (!windowPropertiesContent) {
            // Properties panel doesn't exist on this page
            return;
        }

        windowPropertiesContent.innerHTML = `
            <div class="property-row">
                <span class="property-label">Title:</span>
                <span class="property-value">${windowInfo.title || 'Unknown'}</span>
            </div>
            <div class="property-row">
                <span class="property-label">Process:</span>
                <span class="property-value">${windowInfo.owner || 'Unknown'}</span>
            </div>
            <div class="property-row">
                <span class="property-label">PID:</span>
                <span class="property-value">${windowInfo.pid || 'N/A'}</span>
            </div>
            <div class="property-row">
                <span class="property-label">Handle:</span>
                <span class="property-value">${windowInfo.hwnd || 'N/A'}</span>
            </div>
            <div class="property-row">
                <span class="property-label">Position:</span>
                <span class="property-value">${windowInfo.x}, ${windowInfo.y}</span>
            </div>
            <div class="property-row">
                <span class="property-label">Size:</span>
                <span class="property-value">${windowInfo.width} × ${windowInfo.height}</span>
            </div>

            <div class="properties-actions">
                <div class="property-row">
                    <span class="property-label">New Title:</span>
                    <input type="text" class="property-input" id="newWindowTitle" placeholder="Enter new title..." />
                </div>
                <button class="action-button primary" id="setTitleBtn">Set Window Title</button>
                <button class="action-button warning" id="disableCloseBtn">Disable Close Button</button>
                <button class="action-button warning" id="enableCloseBtn">Enable Close Button</button>
                <button class="action-button danger" id="closeWindowBtn">Close Window</button>

                <div class="property-row" style="margin-top: 15px; padding-top: 15px; border-top: 1px solid #ddd;">
                    <span class="property-label">Visual Effects:</span>
                    <select class="property-input" id="effectSelect" style="cursor: pointer;">
                        <option value="none">None</option>
                        <option value="crt">CRT Display</option>
                        <option value="corrupted">Corrupted Pixels</option>
                        <option value="color-invert">Color Invert Flash</option>
                        <option value="melting">Melting</option>
                    </select>
                </div>
                <button class="action-button primary" id="applyEffectBtn">Apply Effect</button>
                <button class="action-button secondary" id="removeEffectBtn">Remove Effect</button>
            </div>
        `;

        // Add event listeners to the buttons
        document.getElementById('setTitleBtn').addEventListener('click', () => this.setWindowTitle());
        document.getElementById('disableCloseBtn').addEventListener('click', () => this.disableCloseButton());
        document.getElementById('enableCloseBtn').addEventListener('click', () => this.enableCloseButton());
        document.getElementById('closeWindowBtn').addEventListener('click', () => this.closeWindow());
        document.getElementById('applyEffectBtn').addEventListener('click', () => this.applyWindowEffect());
        document.getElementById('removeEffectBtn').addEventListener('click', () => this.removeWindowEffect());
    }

    applyWindowEffect() {
        const effectType = document.getElementById('effectSelect').value;

        if (effectType === 'none') {
            alert('Please select an effect');
            return;
        }

        if (!this.selectedWindow || !this.selectedWindow.hwnd) {
            alert('No window selected');
            return;
        }

        console.log(`[WindowControl] Applying ${effectType} effect to window ${this.selectedWindow.hwnd}`);

        // Send via feature message protocol to window_effects feature
        const message = {
            feature: 'window-effects',
            version: '1.0',
            cmd: 'apply_effect',
            payload: {
                hwnd: this.selectedWindow.hwnd,
                effectType: effectType
            },
            targetPeerId: this.getTargetPeerId()
        };

        // Send via core API
        this.core.sendFeatureMessage(message);
    }

    removeWindowEffect() {
        if (!this.selectedWindow || !this.selectedWindow.hwnd) {
            alert('No window selected');
            return;
        }

        console.log(`[WindowControl] Removing effect from window ${this.selectedWindow.hwnd}`);

        // Send via feature message protocol to window_effects feature
        const message = {
            feature: 'window-effects',
            version: '1.0',
            cmd: 'remove_effect',
            payload: {
                hwnd: this.selectedWindow.hwnd
            },
            targetPeerId: this.getTargetPeerId()
        };

        // Send via core API
        this.core.sendFeatureMessage(message);

        // Reset dropdown
        document.getElementById('effectSelect').value = 'none';
    }

    generateMessageId() {
        return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
    }

    setWindowTitle() {
        const newTitle = document.getElementById('newWindowTitle').value.trim();
        if (!newTitle) {
            alert('Please enter a new title');
            return;
        }

        if (!this.selectedWindow || !this.selectedWindow.hwnd) {
            alert('No window selected');
            return;
        }

        console.log('[WindowControl] Setting window title to:', newTitle);
        this.sendMessage('set_window_title', {
            hwnd: this.selectedWindow.hwnd,
            title: newTitle
        }, this.getTargetPeerId());
    }

    disableCloseButton() {
        if (!this.selectedWindow || !this.selectedWindow.hwnd) {
            alert('No window selected');
            return;
        }

        console.log('[WindowControl] Disabling close button');
        this.sendMessage('disable_close_button', {
            hwnd: this.selectedWindow.hwnd
        }, this.getTargetPeerId());
    }

    enableCloseButton() {
        if (!this.selectedWindow || !this.selectedWindow.hwnd) {
            alert('No window selected');
            return;
        }

        console.log('[WindowControl] Enabling close button');
        this.sendMessage('enable_close_button', {
            hwnd: this.selectedWindow.hwnd
        }, this.getTargetPeerId());
    }

    closeWindow() {
        if (!this.selectedWindow || !this.selectedWindow.hwnd) {
            alert('No window selected');
            return;
        }

        if (!confirm(`Are you sure you want to close "${this.selectedWindow.title}"?`)) {
            return;
        }

        console.log('[WindowControl] Closing window');
        this.sendMessage('close_window', {
            hwnd: this.selectedWindow.hwnd
        }, this.getTargetPeerId());

        // Clear properties panel if it exists
        const windowPropertiesContent = this.getWindowPropertiesContent();
        if (windowPropertiesContent) {
            windowPropertiesContent.innerHTML = '<p class="no-window-selected">Window closed</p>';
        }
        this.selectedWindow = null;
    }

    setupDragHandlers(dragHandleElement, videoElement) {
        const videoWrapper = videoElement.parentElement;

        let dragState = {
            isDragging: false,
            startClientX: 0,
            startClientY: 0,
            windowStartX: 0,
            windowStartY: 0,
            hwnd: null
        };

        const onMouseDown = (e) => {
            e.preventDefault();
            e.stopPropagation();

            // Read current position from this.selectedWindow at time of mousedown
            if (!this.selectedWindow) {
                console.warn('[WindowControl] No selected window when starting drag');
                return;
            }

            // Store drag start position in client coordinates
            dragState.isDragging = true;
            dragState.startClientX = e.clientX;
            dragState.startClientY = e.clientY;
            dragState.windowStartX = this.selectedWindow.x;
            dragState.windowStartY = this.selectedWindow.y;
            dragState.hwnd = this.selectedWindow.hwnd;

            console.log('[WindowControl] Started dragging window', dragState.hwnd, 'from position', dragState.windowStartX, dragState.windowStartY);

            // Change cursor to grabbing
            dragHandleElement.style.cursor = 'grabbing';
            document.body.style.cursor = 'grabbing';

            // Add mousemove and mouseup listeners to document
            document.addEventListener('mousemove', onMouseMove);
            document.addEventListener('mouseup', onMouseUp);
        };

        const onMouseMove = (e) => {
            if (!dragState.isDragging) return;

            e.preventDefault();

            // Get video dimensions and scale using native resolution
            const rect = videoElement.getBoundingClientRect();
            const sourceWidth = this.nativeScreenWidth || videoElement.videoWidth;
            const sourceHeight = this.nativeScreenHeight || videoElement.videoHeight;
            const scaleX = sourceWidth / rect.width;
            const scaleY = sourceHeight / rect.height;

            // Calculate delta in client coordinates
            const deltaClientX = e.clientX - dragState.startClientX;
            const deltaClientY = e.clientY - dragState.startClientY;

            // Convert delta to screen coordinates
            const deltaScreenX = deltaClientX * scaleX;
            const deltaScreenY = deltaClientY * scaleY;

            // Calculate new window position in screen coordinates
            const newWindowX = dragState.windowStartX + deltaScreenX;
            const newWindowY = dragState.windowStartY + deltaScreenY;

            // Update local window info in this.selectedWindow
            if (this.selectedWindow && this.selectedWindow.hwnd === dragState.hwnd) {
                this.selectedWindow.x = newWindowX;
                this.selectedWindow.y = newWindowY;
            }

            // Update all visual elements immediately for responsive feedback
            const outline = videoWrapper.querySelector(`.window-outline[data-hwnd="${dragState.hwnd}"]`);
            const label = videoWrapper.querySelector(`.window-label[data-hwnd="${dragState.hwnd}"]`);
            const shade = videoWrapper.querySelector(`.window-shade[data-hwnd="${dragState.hwnd}"]`);
            const handle = videoWrapper.querySelector(`.window-drag-handle[data-hwnd="${dragState.hwnd}"]`);

            // Convert to display coordinates
            const displayX = newWindowX / scaleX;
            const displayY = newWindowY / scaleY;
            const displayWidth = this.selectedWindow ? this.selectedWindow.width / scaleX : 0;
            const displayHeight = this.selectedWindow ? this.selectedWindow.height / scaleY : 0;

            if (outline) {
                outline.style.left = `${displayX}px`;
                outline.style.top = `${displayY}px`;
            }
            if (label) {
                label.style.left = `${displayX}px`;
                label.style.top = `${Math.max(0, displayY - 25)}px`;
            }
            if (shade) {
                shade.style.left = `${displayX}px`;
                shade.style.top = `${displayY}px`;
            }
            if (handle) {
                handle.style.left = `${displayX + displayWidth / 2 - 20}px`;
                handle.style.top = `${displayY + displayHeight - 35}px`;
            }
        };

        const onMouseUp = (e) => {
            if (!dragState.isDragging) return;

            e.preventDefault();

            console.log('[WindowControl] Stopped dragging window - sending final position');

            dragState.isDragging = false;
            dragHandleElement.style.cursor = 'grab';
            document.body.style.cursor = '';

            // Remove listeners
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);

            // Calculate final position using native resolution
            const rect = videoElement.getBoundingClientRect();
            const sourceWidth = this.nativeScreenWidth || videoElement.videoWidth;
            const sourceHeight = this.nativeScreenHeight || videoElement.videoHeight;
            const scaleX = sourceWidth / rect.width;
            const scaleY = sourceHeight / rect.height;

            const deltaClientX = e.clientX - dragState.startClientX;
            const deltaClientY = e.clientY - dragState.startClientY;
            const deltaScreenX = deltaClientX * scaleX;
            const deltaScreenY = deltaClientY * scaleY;

            const newWindowX = Math.round(dragState.windowStartX + deltaScreenX);
            const newWindowY = Math.round(dragState.windowStartY + deltaScreenY);

            console.log(`[WindowControl] Sending move command to (${newWindowX}, ${newWindowY})`);

            // Send single move command to desktop
            this.sendMessage('move_window', {
                hwnd: dragState.hwnd,
                x: newWindowX,
                y: newWindowY
            }, this.getTargetPeerId());

            // Update stored position
            if (this.selectedWindow && this.selectedWindow.hwnd === dragState.hwnd) {
                this.selectedWindow.x = newWindowX;
                this.selectedWindow.y = newWindowY;
            }
        };

        // Add mousedown listener to drag handle
        dragHandleElement.addEventListener('mousedown', onMouseDown);
    }

    async cleanup() {
        console.log('[WindowControl] Cleaning up...');

        // Remove escape key listener
        if (this.handleEscapeKey) {
            document.removeEventListener('keydown', this.handleEscapeKey);
        }

        // Stop focus time tracking
        this.stopFocusTimeTracking();
        this.windowFocusTimes.clear();

        this.clearAllOutlines();
        this.selectedWindow = null;
        this.selectedWindowPeerId = null;
    }
}
